/**
 * SeatSelector — ARIA tablist of seat chips reading from SEATS (live only).
 * Controlled: the parent owns activeId. flex-wrap guarantees no horizontal
 * scroll at 390px. Roving tabindex + Arrow/Home/End keyboard support.
 */

import { useRef } from 'react';
import type { KeyboardEvent } from 'react';
import { LIVE_SEATS } from '../../config/seats.config';
import type { SeatId } from '../../config/seats.config';

/** Shared ids so SeatPanel can wire aria-labelledby without prop drilling. */
export const SEAT_PANEL_ID = 'seat-panel';
export const seatTabId = (id: SeatId): string => `seat-tab-${id}`;

interface SeatSelectorProps {
  activeId: SeatId;
  onSelect: (id: SeatId) => void;
}

export default function SeatSelector({ activeId, onSelect }: SeatSelectorProps) {
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const activeIndex = Math.max(
    0,
    LIVE_SEATS.findIndex((seat) => seat.id === activeId),
  );

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    let nextIndex: number;
    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (activeIndex + 1) % LIVE_SEATS.length;
        break;
      case 'ArrowLeft':
        nextIndex = (activeIndex - 1 + LIVE_SEATS.length) % LIVE_SEATS.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = LIVE_SEATS.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    const next = LIVE_SEATS[nextIndex];
    if (!next) return;
    onSelect(next.id);
    tabRefs.current[nextIndex]?.focus();
  };

  return (
    <div
      role="tablist"
      aria-label="Choose your seat"
      className="flex flex-wrap gap-1.5"
      onKeyDown={handleKeyDown}
    >
      {LIVE_SEATS.map((seat, index) => {
        const selected = seat.id === activeId;
        return (
          <button
            key={seat.id}
            ref={(el) => {
              tabRefs.current[index] = el;
            }}
            type="button"
            role="tab"
            id={seatTabId(seat.id)}
            aria-selected={selected}
            aria-controls={SEAT_PANEL_ID}
            tabIndex={selected ? 0 : -1}
            onClick={() => onSelect(seat.id)}
            className={[
              'inline-flex items-center rounded-none border py-2.5 pr-3.5 font-mono text-[10px] uppercase tracking-[0.1em] transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
              // Active = cream text + an orange left tick (2px border-left); inactive = muted.
              selected
                ? 'border-l-2 bg-surface pl-[calc(0.875rem-1px)] text-text-primary'
                : 'border-l pl-3.5 text-text-muted hover:text-text-primary',
            ].join(' ')}
            style={{
              borderColor: 'var(--color-hairline)',
              borderLeftColor: selected ? 'var(--color-brand)' : 'var(--color-hairline)',
            }}
          >
            {seat.label}
          </button>
        );
      })}
    </div>
  );
}
